import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Users, UserCog, CheckCircle, Clock, TrendingUp } from "lucide-react";
import { StatsGrid } from "../components/admin/StatsGrid";
import { ArchetypeChart } from "../components/admin/ArchetypeChart";
import { UsersTable } from "../components/admin/UsersTable";

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [filters, setFilters] = useState({ organizations: [], departments: [], archetypes: [] });
    const [search, setSearch] = useState("");
    const [orgFilter, setOrgFilter] = useState("all");
    const [deptFilter, setDeptFilter] = useState("all");
    const [archetypeFilter, setArchetypeFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const { impersonate } = useAuth();

    useEffect(() => {
        Promise.all([
            api.get("/admin/dashboard"),
            api.get("/admin/users"),
            api.get("/admin/filters")
        ]).then(([dashRes, usersRes, filtersRes]) => {
            setStats(dashRes.data);
            setUsers(usersRes.data);
            setFilters(filtersRes.data);
        }).catch(console.error).finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleExportPDF = async (userId) => {
        try {
            const response = await api.get(`/reports/${userId}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `report_${userId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) { console.error("PDF download failed", err); }
    };

    const handleImpersonate = async (userId) => {
        try {
            const data = await impersonate(userId);
            if (data.role === "coach") window.location.href = "/coach";
            else window.location.href = "/assessment";
        } catch (err) { console.error("Impersonation failed", err); }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        </div>
    );

    const statCards = [
        { label: "Total Coaches", value: stats?.total_coaches || 0, icon: UserCog, color: "#4A7AB5" },
        { label: "Total Users", value: stats?.total_users || 0, icon: Users, color: "#F5A623" },
        { label: "Completed", value: stats?.total_completed || 0, icon: CheckCircle, color: "#5BA88E" },
        { label: "Pending", value: stats?.total_pending || 0, icon: Clock, color: "#94A3B8" },
        { label: "Avg. Score", value: stats?.average_score || 0, icon: TrendingUp, color: "#8CAED6" },
    ];

    const archetypeData = stats ? Object.entries(stats.archetype_distribution || {})
        .map(([name, count]) => ({ name, value: count }))
        .sort((a, b) => b.value - a.value) : [];

    return (
        <div data-testid="admin-dashboard" className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6 sm:space-y-8">
            <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground" style={{ fontFamily: "Outfit" }}>
                    Admin Dashboard
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Platform-wide overview of coachability assessments</p>
            </div>

            <StatsGrid statCards={statCards} />
            <ArchetypeChart archetypeData={archetypeData} />
            <UsersTable
                users={users} filters={filters}
                search={search} setSearch={setSearch}
                orgFilter={orgFilter} setOrgFilter={setOrgFilter}
                deptFilter={deptFilter} setDeptFilter={setDeptFilter}
                archetypeFilter={archetypeFilter} setArchetypeFilter={setArchetypeFilter}
                onExportPDF={handleExportPDF} onImpersonate={handleImpersonate}
            />
        </div>
    );
}
